import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Logger } from '../utils/Logger';
import { trackEvent } from './AppInsightsPlugin';

/**
 * HTTP client configuration callbacks
 * These are injected by the ApiServiceProvider to handle auth state and errors
 */
export interface HttpClientCallbacks {
  setAuthBailleurId: (id: number | undefined, reason: string) => void;
  clearAuthState: (reason: string) => void;
}

/**
 * HTTP helper functions for making requests
 * These are wrappers around axios that handle cancellation
 */
export interface ApiHelpers {
  apiGet<TResult>(url: string, config?: AxiosRequestConfig): Promise<TResult | undefined>;
  apiPost<TResult, TData>(url: string, data: TData, config?: AxiosRequestConfig): Promise<TResult | undefined>;
  apiDelete<TResult>(url: string, config?: AxiosRequestConfig): Promise<TResult | undefined>;
}

/**
 * Create and configure the HTTP client with interceptors
 *
 * Responsibilities:
 * - Create axios instance with base URL and credentials
 * - Setup response interceptor for auth headers and error handling
 * - Setup error interceptor for API errors
 *
 * @param callbacks - Auth and error callbacks from the provider
 * @returns Configured axios instance
 */
export function createHttpClient(callbacks: HttpClientCallbacks): AxiosInstance {
  const axiosClient: AxiosInstance = axios.create({
    baseURL: '/api',
    withCredentials: true
  });

  // Setup response interceptor
  axiosClient.interceptors.response.use(
    (response) => {

      // Handle bailleurId from header: set if present
      // NOTE: Some responses may not have this header (e.g., cached responses, 204 No Content)
      // Only update if header is explicitly present
      const bailleurIdHeader = response.headers['x-bailleur-id'];
      if (bailleurIdHeader) {
        const parsedId = parseInt(bailleurIdHeader, 10);
        if (!Number.isNaN(parsedId)) {
          callbacks.setAuthBailleurId(parsedId, `response from ${response.config.url}`);
        }
      }
      // Note: logout() explicitly clears auth state via try-finally block

      return response;
    },
    (error) => {
      // Ignore cancellation errors (AbortController)
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      // 401: Unauthorized
      if (error.response?.status === 401) {
        Logger.error('ApiService', '❌ HTTP 401 UNAUTHORIZED');
        callbacks.clearAuthState('HTTP 401 - Unauthorized');
        trackEvent('API_ERROR_401', {
          url: error.config?.url,
          method: error.config?.method,
          endpoint: error.config?.url?.replace(/^\/api\//, '')
        });
        return Promise.reject(error);
      }

      // Network error or server unreachable
      if (!error.response) {
        // Propagate error to caller for handling
        return Promise.reject(error);
      }

      // 400: Bad Request
      if (error.response.status === 400) {
        return Promise.reject(error);
      }

      // 5xx: Server error
      if (error.response.status >= 500) {
        // Propagate error to caller for handling
        return Promise.reject(error);
      }

      // 4xx: Other client errors
      if (error.response.status >= 400) {
        // Propagate error to caller for handling
        return Promise.reject(error);
      }

      // Unexpected error - propagate to caller
      return Promise.reject(error);
    }
  );

  return axiosClient;
}

/**
 * Create HTTP helper functions for making requests
 *
 * These functions wrap axios to provide a consistent interface and handle cancellation
 *
 * @param client - The configured axios instance
 * @returns Object with apiGet, apiPost, apiDelete helper functions
 */
export function createApiHelpers(client: AxiosInstance): ApiHelpers {
  const apiPost = async <TResult, TData>(url: string, data: TData, config: AxiosRequestConfig<TData> | undefined): Promise<TResult | undefined> => {
    let response: AxiosResponse<TResult, TData> | undefined = undefined;
    await client.post<TResult, AxiosResponse<TResult, TData>, TData>(url, data, config)
      .then(r => {
        response = r;
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
    if (!response) {
      return undefined;
    }
    return (response as AxiosResponse<TResult, TData>).data;
  };

  const apiDelete = async <TResult, TData>(url: string, config: AxiosRequestConfig<TData> | undefined): Promise<TResult | undefined> => {
    let response: AxiosResponse<TResult, TData> | undefined = undefined;
    await client.delete<TResult, AxiosResponse<TResult, TData>, TData>(url, config)
      .then(r => {
        response = r;
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
    if (!response) {
      return undefined;
    }
    return (response as AxiosResponse<TResult, TData>).data;
  };

  const apiGet = async <TResult, D = any>(url: string, config: AxiosRequestConfig<D> | undefined): Promise<TResult | undefined> => {
    let response: AxiosResponse<TResult, D> | undefined = undefined;
    await client.get<TResult, AxiosResponse<TResult, D>, D>(url, config)
      .then(r => {
        response = r;
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          throw error;
        }
      });
    if (!response) {
      return undefined;
    }
    return (response as AxiosResponse<TResult, D>).data;
  };

  return { apiGet, apiPost, apiDelete };
}
